import type { Task } from "~skillBuilder/utils/types";

import { image, title } from "../../contentBlocks";

export const PSkillCheck = {
  name: "Skill Check",
  uniqueName: "p-skill-check",
  icon: "BookmarkCheck",
  durationTimeText: "15 min",
  isQuiz: true,
  steps: [
    {
      nextButtonText: "Start",
      prevButtonText: "Review",
      showPreviousButton: true,
      questions: [
        {
          attribute: "professionalism.skill-check/intro",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Skill Check", durationTimeText: "10 min" })}
<p>Show what you've learned! Complete this quiz to earn a qualification for your profile.</p>
<div style="text-align:center;max-width:250px;width:100%;margin-top:24px;margin-left:auto;margin-right:auto;"/>
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/v1731113489/skillbuilderAssets/optimized/woman-and-clipboard.png", alt: "illustrated woman standing in front of a checked clipboard" })}
</div>
`,
          },
        },
      ],
    },
    {
      questions: [
        {
          attribute: "professionalism.skill-check/qualities",
          label: "Which quality demonstrates a professional image as a substitute teacher?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Using personal phone during class time",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Arriving on-time and prepared for the day",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Giving vague instructions to students",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Disregarding or not adhering to school policies",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    {
      questions: [
        {
          attribute: "professionalism.skill-check/first-impression",
          label: "What is one key way to make a strong first impression?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Arriving late to a request",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Dressing casually in shorts",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Being actively engaged with the students and staff",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Avoiding interaction with the students or staff",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    {
      questions: [
        {
          attribute: "professionalism.skill-check/communication",
          label: "Which of the following demonstrates professional communication?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Raising your voice to get students' attention",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Engaging in conversations unrelated to the lesson plan or learning",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Discussing topics that may not be age-appropriate for students",
                value: "invalid-3",
                isCorrect: false,
              },
              {
                label: "Using sarcasm or name-calling to manage behavior",
                value: "invalid-4",
                isCorrect: false,
              },
              {
                label:
                  "Demonstrating active listening and maintaining a positive, solution-focused approach",
                value: "valid",
                isCorrect: true,
              },
            ],
          },
        },
      ],
    },
    {
      questions: [
        {
          attribute: "professionalism.skill-check/tech",
          label:
            "What is an effective strategy for handling conflicts in the classroom professionally?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label: "Offering choices to guide students toward a solution",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Raising your voice to establish control",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Engaging in a power struggle with a student",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Focusing on the challenging situation instead of moving forward",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    {
      questions: [
        {
          attribute: "professionalism.skill-check/leaving",
          label:
            "Which of the following actions is recommended before leaving a classroom for the day?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label:
                  "Once the day is finished, no action is necessary to take when leaving the classroom",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label:
                  "Write a detailed note to the regular teacher about what was covered and any issues that arose and leave the room better than you found it",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "Engage students in a long conversation before leaving the room",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label: "Ignore classroom organization and leave the room as is",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    {
      questions: [
        {
          attribute: "professionalism.skill-check/reflection",
          label: "Which reflection technique can help you grow in your practice?",
          answerType: {
            type: "RADIO",
            options: [
              {
                label:
                  "Ignoring feedback and continuing the same approach regardless of the outcome",
                value: "invalid-1",
                isCorrect: false,
              },
              {
                label: "Only reflecting on successful moments and avoiding difficult situations",
                value: "invalid-2",
                isCorrect: false,
              },
              {
                label:
                  "Asking yourself questions about your communication, adaptability, and interactions with students",
                value: "valid",
                isCorrect: true,
              },
              {
                label: "By only focusing on positive experiences and ignoring challenges",
                value: "invalid-3",
                isCorrect: false,
              },
            ],
          },
        },
      ],
    },
    {
      nextButtonText: "Great!",
      questions: [
        {
          attribute: "professionalism.skill-check/outro",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<div style="text-align: center;">
  ${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1731608485/skillbuilderAssets/optimized/skill_builder_complete.jpg", alt: "illustrated man holding trophy standing next to a woman" })}
  <p><strong>You've completed this skill!</strong></p>
  <p>Thank you for your commitment to developing your teaching skills. You'll now see <strong>Professionalism</strong> as a qualification on your profile.</p>
</div>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
